<script setup lang="ts">
import { ButtonVariant, OrButtonV3 as OrButton } from '@onereach/ui-components';
import { AccountData } from '@or-sdk/accounts';
import { nextTick, onMounted, onUnmounted, ref } from 'vue';

import useI18n from '@/composables/useI18n';
import { useAuthStore } from '@/stores/auth/auth';
import { authUiUrl, sdkApiUrl } from '@/utils/authUiRuntime';
import { isElectronEnvironment, isIOSWebView } from '@/utils/environmnentCheckers';

import config from '../../config';
import { useErrorHandler } from '../../utils/error';
import { waitForWindowClose } from '../../utils/sso';
const {
  showError,
} = useErrorHandler();
const { translate } = useI18n();

const props = withDefaults(defineProps<{
  verificationCode?: string;
  label?: string;
  modelValue?: string;
  showIcon?: boolean;
  buttonType?: `${ButtonVariant}`;
  keepLoggedIn: boolean;
  accountData: AccountData;
  disabled?: boolean;
  fromExpired?: boolean;
}>(), {
  modelValue: '',
  label: '',
  showIcon: true,
  buttonType: ButtonVariant.Outlined,
  verificationCode: '',
  keepLoggedIn: false,
  disabled: false,
  fromExpired: false,
});

const googleLoading = defineModel<boolean>('googleLoading', { default: false });

const emit = defineEmits(['update:modelValue']);

const authStore = useAuthStore();

const GSI_STATE_COOKIE = 'g_state';
const ONE_TAP_BLOCKED_KEY = 'googleOneTapBlockedUntil';
// Chrome's FedCM cooldown grows from 2h to 4w and is invisible to the page, so the Google button is preferred this long
const ONE_TAP_BLOCKED_TTL_MS = 24 * 60 * 60 * 1000;
// GIS renders its button up to this width
const RECOVERY_BUTTON_MAX_WIDTH = 400;
const ONE_TAP_CLOSE_REASONS = ['user_cancel', 'auto_cancel', 'tap_outside'];
const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
// Google requires redirect mode for its button on iOS, and embedded login leaves One Tap to the host page
const canRenderRecoveryButton = !isIOS && window.self === window.top;

const showRecovery = ref(false);
const root = ref<HTMLElement | null>(null);
const recoveryButton = ref<HTMLElement | null>(null);
let gsiInitialized = false;
let ssoPopupPending = false;
let promptPending = false;
let oneTapBlocked = false;

function markOneTapBlocked() {
  oneTapBlocked = true;
  if (canRenderRecoveryButton) localStorage.setItem(ONE_TAP_BLOCKED_KEY, `${Date.now() + ONE_TAP_BLOCKED_TTL_MS}`);
}

function isOneTapBlockedRemembered() {
  return canRenderRecoveryButton && Number(localStorage.getItem(ONE_TAP_BLOCKED_KEY)) > Date.now();
}

async function initCallback({ credential }: { credential: string; }) {
  promptPending = false;
  oneTapBlocked = false;
  localStorage.removeItem(ONE_TAP_BLOCKED_KEY);
  showRecovery.value = false;
  googleLoading.value = true;
  emit('update:modelValue', credential);
  try {
    await authStore.signIn({
      googleCode: credential,
      keepLoggedIn: props.keepLoggedIn,
      accountData: props.accountData,
    });
  } catch (e) {
    showError(e as string);
  } finally {
    googleLoading.value = false;
  }
}

function initGsi() {
  if (gsiInitialized) return;
  // @ts-expect-error its imported in index.html
  // eslint-disable-next-line
  google.accounts.id.initialize({
    client_id: config.GOOGLE_APP_ID,
    callback: initCallback,
    use_fedcm_for_button: true,
  });
  gsiInitialized = true;
}

function cancelOneTap() {
  if (!gsiInitialized) return;
  // @ts-expect-error its imported in index.html
  // eslint-disable-next-line
  google.accounts.id.cancel();
}

function resetSsoLoading() {
  ssoPopupPending = false;
  document.removeEventListener('visibilitychange', onLoginTabVisible);
  googleLoading.value = false;
}

// on mobile the SSO window is a tab, coming back to the login tab must not leave the form locked
function onLoginTabVisible() {
  if (document.visibilityState === 'visible' && ssoPopupPending) googleLoading.value = false;
}

function googleFallback() {
  let url = `${config.GLOBAL_SSO_URL}?GATEWAY_URL=${config.GATEWAY_URL}&AUTH_UI_URL=${authUiUrl}&PROVIDERS_ACCOUNT_ID=${config.PROVIDERS_ACCOUNT_ID}&SDK_API_URL=${sdkApiUrl}`;
  if (props.verificationCode) url += `&MFACode=${props.verificationCode}`;

  if (isIOSWebView() || isElectronEnvironment()) {
    localStorage.setItem('keepLoggedIn', `${props.keepLoggedIn}`);
    return window.location.href = url;
  }

  const left = Math.max(window.innerWidth / 2 - 300, 0);
  const top = Math.max(window.innerHeight / 2 - 300, 0);

  const ssoWindow = window.open(url, 'SSO', `width=590,height=600,left=${left},top=${top}`);
  if (!ssoWindow) {
    showError(translate('login.login.Google sso unavailable'));
    return null;
  }
  googleLoading.value = true;
  ssoPopupPending = true;
  document.addEventListener('visibilitychange', onLoginTabVisible);
  waitForWindowClose(ssoWindow).then(resetSsoLoading);
  return ssoWindow;
}

async function showRecoveryButton() {
  if (showRecovery.value) return;
  showRecovery.value = true;
  await nextTick();
  const container = recoveryButton.value;
  if (!container) return;
  try {
    initGsi();
    // @ts-expect-error its imported in index.html
    // eslint-disable-next-line
    google.accounts.id.renderButton(container, {
      type: 'standard',
      theme: document.documentElement.dataset.theme === 'dark' ? 'filled_black' : 'outline',
      size: 'large',
      text: 'signin_with',
      width: Math.min(root.value?.clientWidth || RECOVERY_BUTTON_MAX_WIDTH, RECOVERY_BUTTON_MAX_WIDTH),
    });
  } catch (e) {
    showRecovery.value = false;
    showError(e as string);
  }
}

function onOneTapUnavailable() {
  promptPending = false;
  markOneTapBlocked();
  if (canRenderRecoveryButton) return showRecoveryButton();
  showError(translate('login.login.Google prompt unavailable'));
}

function promptCallback(notification) {
  // the host relays this object via postMessage, which strips its methods - momentType, notDisplayed and
  // skippedReason are the agreed contract, h is the minified moment type field of the current GSI build
  const momentType = notification.momentType || notification.getMomentType?.() || notification.h;
  if (momentType === 'display') {
    const notDisplayed = notification.notDisplayed ?? notification.isNotDisplayed?.();
    if (notDisplayed) onOneTapUnavailable();
    else promptPending = false;
    return;
  }
  if (momentType !== 'skipped') return;
  const skippedReason = notification.skippedReason || notification.getSkippedReason?.();
  if (ONE_TAP_CLOSE_REASONS.includes(skippedReason)) {
    promptPending = false;
    return;
  }
  if (skippedReason) return onOneTapUnavailable();
  // FedCM reports a skip without any reason, a closed dialog and a cooldown look the same, so the Google button
  // waits for the next click
  promptPending = false;
  markOneTapBlocked();
}

const openSSOIframe = () => {
  // web view not supports google fedcm api, but old code not working too
  if (/onereach/i.test(navigator.userAgent) || isIOSWebView() || isElectronEnvironment()) { // tmp disabled
    googleFallback();
    return;
  }
  // a click while the previous prompt is still unanswered means Chrome holds the FedCM request in a cooldown and
  // will never notify the page, so this click switches to Google's own button instead of prompting again
  const switchToRecovery = promptPending || oneTapBlocked || isOneTapBlockedRemembered();
  if (window.self !== window.top && !props.fromExpired) { // in iframe
    if (switchToRecovery) onOneTapUnavailable();
    promptPending = true;
    return window.parent.postMessage({ type: 'GOOGLE_LOGIN_PROMPT_OPEN' }, '*');
  }
  if (switchToRecovery) {
    cancelOneTap();
    return onOneTapUnavailable();
  }
  // GIS keeps the One Tap cooldown of legacy browsers in this first-party cookie and re-reads it on every prompt(),
  // an explicit click must get the prompt back after an earlier close
  document.cookie = `${GSI_STATE_COOKIE}=; Max-Age=0; path=/`;
  promptPending = true;
  // no spinner while One Tap is pending - a GSI callback that never comes must not lock the login form
  try {
    initGsi();
    // @ts-expect-error its imported in index.html
    // eslint-disable-next-line
    google.accounts.id.prompt(promptCallback);
  } catch (e) {
    promptPending = false;
    showError(e as string);
  }
};

function handleWindowMessage(event) {
  switch (event.data.type) {
    case 'GOOGLE_INIT_CALLBACK':
      return initCallback(event.data.data);
    case 'GOOGLE_PROMPT_CALLBACK':
      return promptCallback(event.data.data);
  }
}

onMounted(() => {
  window.addEventListener('message', handleWindowMessage);
});

onUnmounted(() => {
  window.removeEventListener('message', handleWindowMessage);
  document.removeEventListener('visibilitychange', onLoginTabVisible);
  googleLoading.value = false;
});
</script>

<template>
  <div
    ref="root"
    class="w-full layout-column gap-md"
  >
    <OrButton
      color="primary"
      class="w-full"
      :variant="buttonType"
      :loading="googleLoading"
      :disabled="disabled"
      @click="openSSOIframe"
    >
      <svg
        v-if="showIcon"
        viewBox="0 0 24 24"
        width="16"
        height="16"
      >
        <g
          xmlns="http://www.w3.org/2000/svg"
          transform="matrix(1, 0, 0, 1, 27.009001, -39.238998)"
        >
          <path
            fill="#4285F4"
            d="M -3.264 51.509 C -3.264 50.719 -3.334 49.969 -3.454 49.239 L -14.754 49.239 L -14.754 53.749 L -8.284 53.749 C -8.574 55.229 -9.424 56.479 -10.684 57.329 L -10.684 60.329 L -6.824 60.329 C -4.564 58.239 -3.264 55.159 -3.264 51.509 Z"
          />
          <path
            fill="#34A853"
            d="M -14.754 63.239 C -11.514 63.239 -8.804 62.159 -6.824 60.329 L -10.684 57.329 C -11.764 58.049 -13.134 58.489 -14.754 58.489 C -17.884 58.489 -20.534 56.379 -21.484 53.529 L -25.464 53.529 L -25.464 56.619 C -23.494 60.539 -19.444 63.239 -14.754 63.239 Z"
          />
          <path
            fill="#FBBC05"
            d="M -21.484 53.529 C -21.734 52.809 -21.864 52.039 -21.864 51.239 C -21.864 50.439 -21.724 49.669 -21.484 48.949 L -21.484 45.859 L -25.464 45.859 C -26.284 47.479 -26.754 49.299 -26.754 51.239 C -26.754 53.179 -26.284 54.999 -25.464 56.619 L -21.484 53.529 Z"
          />
          <path
            fill="#EA4335"
            d="M -14.754 43.989 C -12.984 43.989 -11.404 44.599 -10.154 45.789 L -6.734 42.369 C -8.804 40.429 -11.514 39.239 -14.754 39.239 C -19.444 39.239 -23.494 41.939 -25.464 45.859 L -21.484 48.949 C -20.534 46.099 -17.884 43.989 -14.754 43.989 Z"
          />
        </g>
      </svg>
      &nbsp;
      {{ label || $t('login.password_restore.Sign in') }}
    </OrButton>
    <div
      v-show="showRecovery && !googleLoading && !disabled"
      class="layout-column items-center gap-sm"
    >
      <span class="typography-caption-regular text-center">{{ $t('login.login.Google prompt fallback') }}</span>
      <!-- color-scheme must match the GIS iframe content, otherwise Chrome paints an opaque white box around it -->
      <div
        ref="recoveryButton"
        class="w-full [color-scheme:light]"
      />
    </div>
  </div>
</template>
